import React, { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import styled from 'styled-components'
import IosShareIcon from '@mui/icons-material/IosShare'

import { Button, ButtonTheme } from '@nexiahome/ui-common/components/primitives/buttons'
import LegacyButton from 'components/LegacyButton'
import { ComponentStyleSheet } from '@nexiahome/ui-common/themes'

import LegacyRemoteConfiguration from 'pages/Customer/RemoteConfiguration/LegacyRemoteConfiguration'
import RemoteConfiguration from 'pages/Customer/RemoteConfiguration'
import AssignDevice from 'pages/Customer/AssignDevice'
import CustomerInfo from 'pages/Customer/CustomerInfo'
import Note from 'pages/shared/Note'
import Pin from 'pages/shared/Pin'
import SystemReportsModal from 'pages/Customer/SystemReportsModal'

import { useFeaturesContext } from 'hooks/FeaturesContext'

import { streetAddress, cityStateZip, phoneNumber, customerDisplayName } from 'helpers/formatters'
import GA4 from 'helpers/ga4'

import Api from 'services/api'

import { color } from 'styles/colors'
import { screenSize } from 'styles/shared'

import { Customer } from 'types/Customer'
import { DeviceBase } from 'types/Device'

type Props = {
  customer: Customer,
  activeDevice: DeviceBase | undefined,
  className?: string
  devices: Array<DeviceBase>
  onSaveCallback: (deviceId: string) => void
}
const disableReportForDeviceStatus = {
  'NOT ENROLLED': true,
  'OPTED OUT': true
}

const CustomerDetails = ({ customer, activeDevice, className = '', devices = [], onSaveCallback }: Props) => {
  const navigate = useNavigate()

  const [note, setNote] = useState(customer.note)
  const [showAssignDeviceModel, setShowAssignDeviceModel] = useState(false)
  const [showReportDownloadSettingsModal, setShowReportDownloadSettingsModal] = useState(false)

  const { enabledFeatures } = useFeaturesContext()

  const displayName = customerDisplayName(customer.firstName, customer.lastName, customer.companyName)

  const onEditCusomerClick = () => navigate(`/customers/${customer.id}/edit`)

  const onSaveNote = async (updatedNote: string) => {
    if (updatedNote === note) {
      return true
    }

    const editCustomer: Customer = { ...customer, note: updatedNote }

    GA4.sendEvent({ category: 'Customer Detail Page', action: 'Click Save Customer Note' })

    return await Api.Customer.update(customer.dealerId, customer.id, editCustomer)
      .then(() => {
        setNote(updatedNote)
        return true
      })
      .catch(() => {
        return false
      })
  }

  const onClickDownloadReport = () => {
    setShowReportDownloadSettingsModal(true)
  }

  const allowedDevices = devices?.filter(({ status }) => !disableReportForDeviceStatus[status]) ?? []
  const isReportDownloadEnabled = allowedDevices.length > 0
  return (
    <>
      <S.Container {...{ className }}>
        <S.Row>
          <S.NameContainer>
            <S.Pin isPinned={customer.pinned} pinnedAt={customer.pinnedAt} displayUnpinned customerName={displayName} customerId={customer.id} />
            <S.Name>{displayName}</S.Name>
            <CustomerInfo
              companyName={customer.companyName}
              name={displayName}
              email={customer.email}
              phone={phoneNumber(customer.phone)}
              address={streetAddress(customer.address1, customer.address2)}
              city={cityStateZip(customer.city, customer.state, customer.zip)}
            />

            <S.Note note={note} onSaveNoteCallback={onSaveNote} variant="large" />

            {enabledFeatures.includes('system_reports') && devices.length > 0 && isReportDownloadEnabled && (
              <S.ReportButton onClick={onClickDownloadReport} type="button" variant="link"><IosShareIcon />System Report</S.ReportButton>
            )
          }
          </S.NameContainer>
        </S.Row>

        <S.Row>
          <S.ButtonContainer>
            {enabledFeatures.includes('stream_remote_config') && activeDevice && <S.RemoteConfiguration device={activeDevice} />}
            {!enabledFeatures.includes('stream_remote_config') && activeDevice && <S.LegacyRemoteConfiguration device={activeDevice} />}
            <Button decoration="secondary" icon="edit" text="EDIT CUSTOMER" styles={EditButtonStyle} onPress={onEditCusomerClick} />
            <Button decoration="positive" icon="new" text="ASSIGN SYSTEM" onPress={() => setShowAssignDeviceModel(true)} />
          </S.ButtonContainer>
        </S.Row>
      </S.Container>

      {showAssignDeviceModel && <AssignDevice dealerId={customer.dealerId} customerId={customer.id} onSaveCallback={(deviceId) => onSaveCallback(deviceId)} closeCallback={() => setShowAssignDeviceModel(false)} />}
      {showReportDownloadSettingsModal && enabledFeatures.includes('system_reports') && devices.length > 0 && (
        <SystemReportsModal
          isDisplayed={showReportDownloadSettingsModal}
          closeCallback={() => setShowReportDownloadSettingsModal(false)}
          devices={allowedDevices}
          customer={customer}
        />
      )}
    </>
  )
}

export default CustomerDetails

const EditButtonStyle: ComponentStyleSheet<ButtonTheme> = { button: { marginRight: 15 } }

const S = {
  Container: styled.div`
    display: flex;
    flex-wrap: wrap;
    justify-content: space-between;
    background-color: ${color('paneBackground')};

    @media ${screenSize.small} {
      flex-direction: column;
    }
  `,
  Row: styled.div`
    display: flex;

    @media ${screenSize.small} {
      flex-direction: column;
    }
  `,
  NameContainer: styled.div`
    display: flex;
    align-items: center;

    @media ${screenSize.small} {
      flex-wrap: wrap;
    }
  `,
  Name: styled.h1`
    padding-left: 15px;
    text-transform: capitalize;
    font-size: 2rem;
    font-weight: 600;
    color: ${color('customerNameText')};

    @media ${screenSize.small} {
      margin: 0px;
    }
  `,
  Note: styled(Note)`
    height: 26px;
  `,
  Pin: styled(Pin)`
    font-size: 18px;
    padding: 5px;
  `,
  ButtonContainer: styled.div`
    display: flex;
    align-items: start;
    gap: 10px;

    & > button {
      margin: 0 !important;
      height: 34px !important;
      min-width: 64px !important;
   }

    @media ${screenSize.small} {
      flex-direction: column;
      align-items: center;
    }
  `,
  RemoteConfiguration: styled(RemoteConfiguration)`
    @media ${screenSize.small} {
      grid-row: 2;
      grid-column: 1 / span 2;
    }
  `,
  LegacyRemoteConfiguration: styled(LegacyRemoteConfiguration)`
    @media ${screenSize.small} {
      grid-row: 2;
      grid-column: 1 / span 2;
    }
  `,
  ReportButton: styled(LegacyButton)`
    text-transform: uppercase;
    margin-left: 15px;
    & svg{
      margin-right: 4px;
      position: relative;
      top:5px;
    }
  `
}
